/**
 * Unified Financial Overview Chart Component
 * Combines Daily / Weekly / Monthly views with smooth multi-metric cashflow curves.
 * @module components/dashboard/unified-financial-overview
 */

import { useState, useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  type TooltipItem,
} from "chart.js";
import {
  TrendingUp,
  TrendingDown,
  Scale,
  Calendar,
  Sparkles,
} from "lucide-react";
import { parseApiFloat, formatCurrency } from "@/lib/utils/format";
import { getThemeColors } from "@/lib/utils/chart-config";
import { t } from "@/lib/i18n/messages";
import type { Locale } from "@/types/auth";
import type { FinancialOverview, PeriodMetrics } from "@/types/wallet";
import type { DayOfWeek, MonthName, WeekNumber } from "@/types/common";
import { cn } from "@/lib/utils";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface UnifiedFinancialOverviewProps {
  overview: FinancialOverview;
  locale: Locale;
  isDark: boolean;
}

type PeriodMode = "daily" | "weekly" | "monthly";

export function UnifiedFinancialOverview({
  overview,
  locale,
  isDark,
}: UnifiedFinancialOverviewProps) {
  const [period, setPeriod] = useState<PeriodMode>("monthly");
  const isRTL = locale === "ar";
  const colors = getThemeColors(isDark);

  // ── Extract period-specific metrics and labels ─────────────────────────────
  const { labels, earnings, spendings, net, totals } = useMemo(() => {
    let lbls: string[] = [];
    let items: PeriodMetrics[] = [];

    if (period === "daily") {
      const days: DayOfWeek[] = [
        "Saturday",
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
      ];
      lbls = days.map((day) => t(locale, day.toLowerCase() as any));
      items = days.map((day) => overview.daily_breakdown?.[day] ?? { earnings: "0", spendings: "0", net: "0" });
    } else if (period === "weekly") {
      const weeks: WeekNumber[] = ["Week_1", "Week_2", "Week_3", "Week_4", "Week_5"];
      lbls = weeks.map((week) => t(locale, week.toLowerCase().replace("_", "") as any));
      items = weeks.map((week) => overview.weekly_breakdown?.[week] ?? { earnings: "0", spendings: "0", net: "0" });
    } else {
      const months: MonthName[] = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      lbls = months.map((month) => t(locale, month.toLowerCase() as any));
      items = months.map((month) => overview.monthly_breakdown?.[month] ?? { earnings: "0", spendings: "0", net: "0" });
    }

    const eArr = items.map((it) => parseApiFloat(it.earnings));
    const sArr = items.map((it) => parseApiFloat(it.spendings));
    const nArr = items.map((it) => parseApiFloat(it.net));

    const totalEarned = eArr.reduce((a, b) => a + b, 0);
    const totalSpent = sArr.reduce((a, b) => a + b, 0);
    const totalNet = totalEarned - totalSpent;

    return {
      labels: lbls,
      earnings: eArr,
      spendings: sArr,
      net: nArr,
      totals: {
        earned: totalEarned > 0 ? totalEarned : parseApiFloat(overview.total_earnings),
        spent: totalSpent > 0 ? totalSpent : parseApiFloat(overview.total_spendings),
        net: (totalEarned > 0 || totalSpent > 0) ? totalNet : parseApiFloat(overview.net_balance),
      },
    };
  }, [period, overview, locale]);

  // ── Chart configuration & datasets ─────────────────────────────────────────
  const chartData = useMemo(() => {
    return {
      labels,
      datasets: [
        {
          label: isRTL ? "الواردات والشحن" : "Inflows / Top-ups",
          data: earnings,
          borderColor: "#10B981", // Emerald 500
          backgroundColor: isDark
            ? "rgba(16, 185, 129, 0.12)"
            : "rgba(16, 185, 129, 0.08)",
          borderWidth: 2.5,
          tension: 0.38,
          fill: true,
          pointRadius: 3.5,
          pointHoverRadius: 6,
          pointBackgroundColor: "#10B981",
        },
        {
          label: isRTL ? "المصروفات والإنفاق" : "Spendings / Outflow",
          data: spendings,
          borderColor: "#F43F5E", // Rose 500
          backgroundColor: isDark
            ? "rgba(244, 63, 94, 0.12)"
            : "rgba(244, 63, 94, 0.08)",
          borderWidth: 2.5,
          tension: 0.38,
          fill: true,
          pointRadius: 3.5,
          pointHoverRadius: 6,
          pointBackgroundColor: "#F43F5E",
        },
        {
          label: isRTL ? "صافي الرصيد" : "Net Balance",
          data: net,
          borderColor: "#6366F1", // Indigo 500
          borderWidth: 2,
          borderDash: [5, 5],
          tension: 0.38,
          fill: false,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBackgroundColor: "#6366F1",
        },
      ],
    };
  }, [labels, earnings, spendings, net, isRTL, isDark]);

  const chartOptions = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index" as const,
        intersect: false,
      },
      plugins: {
        legend: {
          display: true,
          position: "top" as const,
          align: (isRTL ? "start" : "end") as "start" | "end",
          rtl: isRTL,
          labels: {
            boxWidth: 12,
            boxHeight: 12,
            usePointStyle: true,
            pointStyle: "circle",
            padding: 16,
            color: colors.text,
            font: {
              family: "inherit",
              size: 12,
              weight: 500,
            },
          },
        },
        tooltip: {
          rtl: isRTL,
          backgroundColor: isDark ? "rgba(15, 23, 42, 0.95)" : "rgba(255, 255, 255, 0.95)",
          titleColor: isDark ? "#F8FAFC" : "#0F172A",
          bodyColor: isDark ? "#CBD5E1" : "#334155",
          borderColor: isDark ? "#334155" : "#E2E8F0",
          borderWidth: 1,
          padding: 12,
          cornerRadius: 10,
          boxPadding: 6,
          callbacks: {
            label: (context: TooltipItem<"line">) => {
              const label = context.dataset.label || "";
              const val = context.parsed.y ?? 0;
              return ` ${label}: ${formatCurrency(val, locale)}`;
            },
          },
        },
      },
      scales: {
        x: {
          reverse: isRTL,
          grid: {
            display: false,
          },
          ticks: {
            color: isDark ? "#94A3B8" : "#64748B",
            font: { size: 11, family: "inherit" },
          },
        },
        y: {
          position: (isRTL ? "right" : "left") as "right" | "left",
          grid: {
            color: isDark ? "rgba(51, 65, 85, 0.4)" : "rgba(226, 232, 240, 0.8)",
          },
          ticks: {
            color: isDark ? "#94A3B8" : "#64748B",
            font: { size: 11, family: "inherit" },
            callback: (value: any) => formatCurrency(value, locale),
          },
        },
      },
    };
  }, [isDark, isRTL, colors.text, locale]);

  return (
    <div className="rounded-2xl border border-border bg-card p-5 md:p-7 shadow-xs space-y-6">
      {/* ─── Top Header & Controls ──────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-1.5 rounded-lg bg-primary/10 text-primary">
              <Sparkles className="w-4 h-4" />
            </span>
            <h2 className="text-lg md:text-xl font-bold text-foreground">
              {isRTL ? "النظرة المالية والسيولة" : "Financial Overview & Cashflow"}
            </h2>
          </div>
          <p className="text-xs md:text-sm text-foreground-secondary mt-1">
            {isRTL
              ? "مقارنة متكاملة بين الإيرادات والمصروفات وصافي الأداء المالي."
              : "Integrated comparison of inflows, spending, and net financial position."}
          </p>
        </div>

        {/* ── Period Selector: Daily / Weekly / Monthly ────────────────── */}
        <div
          className="inline-flex items-center p-1 rounded-xl bg-muted/60 border border-border self-start sm:self-auto"
          role="tablist"
          aria-label="Period Mode"
        >
          <button
            type="button"
            role="tab"
            aria-selected={period === "daily"}
            onClick={() => setPeriod("daily")}
            className={cn(
              "px-3.5 py-1.5 rounded-lg text-xs md:text-sm font-semibold transition-all",
              period === "daily"
                ? "bg-primary text-primary-foreground shadow-xs"
                : "text-foreground-secondary hover:text-foreground"
            )}
          >
            {isRTL ? "يومي" : "Daily"}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={period === "weekly"}
            onClick={() => setPeriod("weekly")}
            className={cn(
              "px-3.5 py-1.5 rounded-lg text-xs md:text-sm font-semibold transition-all",
              period === "weekly"
                ? "bg-primary text-primary-foreground shadow-xs"
                : "text-foreground-secondary hover:text-foreground"
            )}
          >
            {isRTL ? "أسبوعي" : "Weekly"}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={period === "monthly"}
            onClick={() => setPeriod("monthly")}
            className={cn(
              "px-3.5 py-1.5 rounded-lg text-xs md:text-sm font-semibold transition-all",
              period === "monthly"
                ? "bg-primary text-primary-foreground shadow-xs"
                : "text-foreground-secondary hover:text-foreground"
            )}
          >
            {isRTL ? "شهري" : "Monthly"}
          </button>
        </div>
      </div>

      {/* ─── Integrated KPI Summary Cards ─────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
        {/* Inflow Card */}
        <div className="p-4 rounded-xl border border-border bg-background/50 hover:bg-background transition-colors">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-foreground-secondary">
              {isRTL ? "إجمالي الوارد (الشحن)" : "Total Inflows"}
            </span>
            <div className="w-7 h-7 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
              <TrendingUp className="w-4 h-4" />
            </div>
          </div>
          <div className="text-xl md:text-2xl font-bold text-foreground">
            {formatCurrency(totals.earned, locale)}
          </div>
          <p className="text-[11px] text-emerald-600 dark:text-emerald-400 mt-1 flex items-center gap-1">
            <span>●</span>
            <span>{isRTL ? "المبالغ المضافة للمحفظة" : "Funds credited to wallet"}</span>
          </p>
        </div>

        {/* Outflow Card */}
        <div className="p-4 rounded-xl border border-border bg-background/50 hover:bg-background transition-colors">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-foreground-secondary">
              {isRTL ? "إجمالي المنفق (المصروفات)" : "Total Spendings"}
            </span>
            <div className="w-7 h-7 rounded-lg bg-rose-500/10 text-rose-600 dark:text-rose-400 flex items-center justify-center">
              <TrendingDown className="w-4 h-4" />
            </div>
          </div>
          <div className="text-xl md:text-2xl font-bold text-foreground">
            {formatCurrency(totals.spent, locale)}
          </div>
          <p className="text-[11px] text-rose-600 dark:text-rose-400 mt-1 flex items-center gap-1">
            <span>●</span>
            <span>{isRTL ? "الإنفاق على المبادرات والمكافآت" : "Disbursed to initiatives"}</span>
          </p>
        </div>

        {/* Net Cashflow Card */}
        <div className="p-4 rounded-xl border border-border bg-background/50 hover:bg-background transition-colors">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-foreground-secondary">
              {isRTL ? "صافي التدفق" : "Net Cashflow"}
            </span>
            <div className="w-7 h-7 rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
              <Scale className="w-4 h-4" />
            </div>
          </div>
          <div
            className={cn(
              "text-xl md:text-2xl font-bold",
              totals.net >= 0
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-rose-600 dark:text-rose-400"
            )}
          >
            {totals.net >= 0 ? "+" : ""}
            {formatCurrency(totals.net, locale)}
          </div>
          <p className="text-[11px] text-foreground-secondary mt-1 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            <span>
              {period === "daily" && (isRTL ? "معدل الأسبوع الجاري" : "Current week days")}
              {period === "weekly" && (isRTL ? "معدل أسابيع الشهر" : "Weeks of the month")}
              {period === "monthly" && (isRTL ? "المسار السنوي الإجمالي" : "Full annual timeline")}
            </span>
          </p>
        </div>
      </div>

      {/* ─── Interactive Spline Area Chart ────────────────────────────── */}
      <div className="h-[320px] md:h-[360px] w-full pt-2">
        <Line data={chartData} options={chartOptions} />
      </div>
    </div>
  );
}
